/**
 * Kayıtlı aramalar + kimlik rozeti — saf yardımcılar (React/i18n bağımlılığı yok; Node testleri çalıştırır).
 * Sözleşme v1.0 (scratchpad/contracts/saved-search-badge.md):
 *   kind 1 = ev/oda ilanları (Roommate/GetFilter, /ilanlar?tab=homes), kind 2 = ev arkadaşı profilleri (Person/GetFilter, ?tab=roommates)
 *   criteria = { filters: [{ propertyName, operation, value(string) }], sortBy?, sortOrder? } — arama ile aynı filtre modeli
 *   notifyMode 0 = kapalı, 1 = günlük özet (varsayılan); kullanıcı başına en fazla 10 arama.
 */

export const SAVED_SEARCH_KIND = Object.freeze({ LISTING: 1, ROOMMATE: 2 });
export const NOTIFY_MODE = Object.freeze({ OFF: 0, DAILY: 1 });
export const SAVED_SEARCH_MAX = 10;
export const SAVED_SEARCH_NAME_MAX = 80;
export const SAVED_SEARCH_NOTIFICATION_TYPE = 24;
export const SAVED_SEARCHES_PATH = '/kayitli-aramalar';

/** "Yalnızca kimliği doğrulanmış" sunucu filtresi (genel filtre modeli; ilanlarda SQL'e çevrilir) */
export const VERIFIED_FILTER = Object.freeze({ propertyName: 'isIdentityVerified', operation: '==', value: 'true' });

const GENDER_CODES = { male: '201', female: '104', none: '70' };
const GENDER_NAMES = { 201: 'Male', 104: 'Female' };

// Kimlik (Guid) yalnızca güvenli karakterlerden oluşabilir (yol/sorgu enjeksiyonu yok)
const SAFE_ID = /^[A-Za-z0-9_-]{1,64}$/;
export const safeSavedSearchId = (v) => (v != null && SAFE_ID.test(String(v)) ? String(v) : '');

const lower = (s) => String(s ?? '').trim().toLowerCase();

/** Ad/kod → sunucu kodu ("Male" | "male" | 201 | "201" → "201"); tanınmayan → '' */
export const genderCode = (v) => {
  if (v === null || v === undefined || v === '') return '';
  const s = lower(v);
  if (GENDER_CODES[s]) return GENDER_CODES[s];
  return ['201', '104', '70'].includes(s) ? s : '';
};

/** Kod/ad → RoommateFilterComponent değeri ("Male" | "Female" | null) */
export const genderName = (v) => GENDER_NAMES[genderCode(v)] || null;

/** Filtre dizisini sözleşme biçimine getirir (PascalCase alan adları kabul; değer her zaman metin; boş değerler atılır) */
export const normalizeFilters = (filters) => {
  if (!Array.isArray(filters)) return [];
  return filters
    .filter((f) => f && (f.propertyName || f.PropertyName))
    .map((f) => {
      const raw = f.value !== undefined ? f.value : f.Value;
      return {
        propertyName: String(f.propertyName ?? f.PropertyName).trim(),
        operation: String(f.operation ?? f.Operation ?? '==').trim() || '==',
        value: raw === null || raw === undefined ? '' : String(raw).trim(),
      };
    })
    .filter((f) => f.propertyName && f.value !== '');
};

const isVerifiedFilter = (f) => lower(f.propertyName) === 'isidentityverified' && f.operation === '==' && lower(f.value) === 'true';

/** Filtre listesine "yalnızca doğrulanmış" ölçütünü ekler/çıkarır (tekrarsız) */
export const withVerifiedFilter = (filters, on) => {
  const rest = normalizeFilters(filters).filter((f) => lower(f.propertyName) !== 'isidentityverified');
  return on ? [...rest, { ...VERIFIED_FILTER }] : rest;
};

export const hasVerifiedFilter = (filters) => normalizeFilters(filters).some(isVerifiedFilter);

/** İlan araması (redux filters + sıralama) → criteria */
export const listingCriteria = (filters, sortBy, sortOrder) => ({
  filters: normalizeFilters(filters),
  sortBy: sortBy || 'CreatedDate',
  sortOrder: lower(sortOrder) === 'asc' ? 'asc' : 'desc',
});

/**
 * Ev arkadaşı araması yerel durumu → filtreler (canlı arama ve kayıtlı arama aynı listeyi kullanır).
 * Cinsiyet sunucu koduna çevrilir ("Male" → "201"; sunucu sayısal değeri de kabul eder).
 */
export const roommateFilters = ({ country, city, district, gender, verifiedOnly } = {}) => {
  const out = [];
  const add = (propertyName, value) => {
    if (value !== null && value !== undefined && value !== '' && value !== 0 && value !== '0') {
      out.push({ propertyName, operation: '==', value: String(value) });
    }
  };
  add('country', country);
  add('city', city);
  add('district', district);
  add('gender', genderCode(gender));
  if (verifiedOnly) out.push({ ...VERIFIED_FILTER });
  return out;
};

/** Ev arkadaşı araması → criteria (sıralama sunucuda saklanmaz) */
export const roommateCriteria = (state) => ({ filters: roommateFilters(state), sortBy: null, sortOrder: null });

const findValue = (filters, names, ops = ['==']) => {
  const f = filters.find((x) => names.includes(lower(x.propertyName)) && ops.includes(x.operation));
  return f ? f.value : null;
};

const toId = (v) => {
  if (v === null || v === undefined || v === '') return null;
  const n = Number(v);
  return Number.isInteger(n) && n > 0 ? n : null;
};

/** criteria → RoommateSearch yerel durumu */
export const roommateStateFromCriteria = (criteria) => {
  const filters = normalizeFilters(criteria?.filters);
  return {
    country: toId(findValue(filters, ['country'])),
    city: toId(findValue(filters, ['city'])),
    district: toId(findValue(filters, ['district'])),
    gender: genderName(findValue(filters, ['gender', 'preferredgender'])),
    verifiedOnly: filters.some(isVerifiedFilter),
  };
};

const isTrue = (filters, names) => filters.some((f) => names.includes(lower(f.propertyName)) && f.operation === '==' && lower(f.value) === 'true');

/** criteria/redux filtreleri → ilan filtre formu (FilterDrawer) durumu */
export const listingFormFromFilters = (rawFilters) => {
  const filters = normalizeFilters(rawFilters);
  const num = (names, ops) => {
    const v = findValue(filters, names, ops);
    const n = Number(v);
    return v !== null && Number.isFinite(n) ? String(Math.trunc(n)) : '';
  };
  return {
    country: toId(findValue(filters, ['country'])) ?? '',
    city: toId(findValue(filters, ['city'])) ?? '',
    district: toId(findValue(filters, ['district'])) ?? '',
    gender: genderCode(findValue(filters, ['preferredgender', 'gender'])),
    ageStart: num(['preferredagerangestart'], ['>=', '>', '==']),
    ageEnd: num(['preferredagerangeend'], ['<=', '<', '==']),
    rentStart: num(['monthlyrent'], ['>=', '>']),
    rentEnd: num(['monthlyrent'], ['<=', '<']),
    petsAllowed: isTrue(filters, ['petsallowed']),
    smokingAllowed: isTrue(filters, ['smokingallowed']),
    alcoholAllowed: isTrue(filters, ['alcolallowed', 'alcoholallowed']),
    utilitiesIncluded: isTrue(filters, ['utilitiesincluded']),
    verifiedOnly: filters.some(isVerifiedFilter),
  };
};

/** İki filtre listesi aynı ölçütleri mi taşıyor (sıra önemsiz) */
export const sameFilters = (a, b) => {
  const key = (list) =>
    normalizeFilters(list)
      .map((f) => `${lower(f.propertyName)}|${f.operation}|${lower(f.value)}`)
      .sort()
      .join(';');
  return key(a) === key(b);
};

/** Kayıtlı aramanın ölçüt özeti (liste sayfasındaki etiketler için; metinleri bileşen üretir) */
export const summarizeCriteria = (criteria) => {
  const filters = normalizeFilters(criteria?.filters);
  const numVal = (names, ops) => {
    const v = findValue(filters, names, ops);
    const n = Number(v);
    return v !== null && Number.isFinite(n) ? n : null;
  };
  const flags = [];
  if (isTrue(filters, ['petsallowed'])) flags.push('petsAllowed');
  if (isTrue(filters, ['smokingallowed'])) flags.push('smokingAllowed');
  if (isTrue(filters, ['alcolallowed', 'alcoholallowed'])) flags.push('alcoholAllowed');
  if (isTrue(filters, ['utilitiesincluded'])) flags.push('utilitiesIncluded');
  return {
    count: filters.length,
    hasLocation: filters.some((f) => ['country', 'city', 'district'].includes(lower(f.propertyName))),
    rentMin: numVal(['monthlyrent'], ['>=', '>']),
    rentMax: numVal(['monthlyrent'], ['<=', '<']),
    ageMin: numVal(['preferredagerangestart'], ['>=', '>', '==']),
    ageMax: numVal(['preferredagerangeend'], ['<=', '<', '==']),
    gender: genderName(findValue(filters, ['gender', 'preferredgender'])),
    verifiedOnly: filters.some(isVerifiedFilter),
    flags,
  };
};

const formatTl = (n) => {
  try {
    return new Intl.NumberFormat('tr-TR', { maximumFractionDigits: 0 }).format(n);
  } catch {
    return String(n);
  }
};

/**
 * Varsayılan arama adı: "<tür> · <ilçe, il> · <kira aralığı> · <doğrulanmış>" (en fazla 80 karakter).
 * names: { cityName, districtName }; labels: { kind, verified, currency } — metinler çağıranın dilinde.
 */
export const defaultSearchName = (criteria, names = {}, labels = {}) => {
  const s = summarizeCriteria(criteria);
  const parts = [];
  if (labels.kind) parts.push(labels.kind);
  const place = [names.districtName, names.cityName].filter(Boolean).join(', ');
  if (place) parts.push(place);
  const cur = labels.currency || '₺';
  if (s.rentMin != null && s.rentMax != null) parts.push(`${formatTl(s.rentMin)}–${formatTl(s.rentMax)} ${cur}`);
  else if (s.rentMax != null) parts.push(`≤ ${formatTl(s.rentMax)} ${cur}`);
  else if (s.rentMin != null) parts.push(`≥ ${formatTl(s.rentMin)} ${cur}`);
  if (s.verifiedOnly && labels.verified) parts.push(labels.verified);
  const name = parts.join(' · ').trim();
  return name.length > SAVED_SEARCH_NAME_MAX ? name.slice(0, SAVED_SEARCH_NAME_MAX).trim() : name;
};

/** Ad doğrulaması: kırpılmış, kontrol karakterlerinden arındırılmış, en fazla 80 karakter */
export const cleanSearchName = (name) =>
  String(name ?? '')
    // eslint-disable-next-line no-control-regex
    .replace(/[\u0000-\u001f\u007f]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .slice(0, SAVED_SEARCH_NAME_MAX);

/** Kayıtlı aramayı arama sayfasında açan yol */
export const savedSearchSearchPath = (saved) => {
  const id = safeSavedSearchId(saved?.id);
  const tab = Number(saved?.kind) === SAVED_SEARCH_KIND.ROOMMATE ? 'roommates' : 'homes';
  return `/ilanlar?tab=${tab}${id ? `&savedSearch=${encodeURIComponent(id)}` : ''}`;
};

/** Bildirim (type 24) → kayıtlı aramalar sayfası; data.sid güvenli değilse vurgusuz liste */
export const savedSearchNotificationPath = (sid) => {
  const id = safeSavedSearchId(sid);
  return id ? `${SAVED_SEARCHES_PATH}?sid=${encodeURIComponent(id)}` : SAVED_SEARCHES_PATH;
};

/** Liste yanıtı → { items, max } (sözleşme: data = { items, max }; dizi gelirse de tolere edilir) */
export const parseSavedSearchList = (data) => {
  if (Array.isArray(data)) return { items: data, max: SAVED_SEARCH_MAX };
  const items = Array.isArray(data?.items) ? data.items : [];
  const max = Number.isInteger(data?.max) && data.max > 0 ? data.max : SAVED_SEARCH_MAX;
  return { items, max };
};
